/**
 * Boost AI Search & Filter → theme integration
 * See sources/docs/boost-turbo-integration.md.
 *
 * 1. Custom navigation: moves the theme-rendered `lindy-custom-navigation`
 *    snippet (which needs Shopify Liquid access to menus/collections) into
 *    Boost's vertical filter tree, and keeps it there across Boost re-renders.
 *
 * 2. Theme cart drawer: when a Boost add-to-cart succeeds, refreshes the
 *    theme's cart (cart icon + drawer contents via the `cart:update` event)
 *    and opens the theme's cart drawer instead of Boost's ajax cart.
 *    Requires Boost admin: Integration > Edit Visual Design > Product item >
 *    Ajax cart > display type "None".
 *
 * 3. Volume pricing hydration: B2B quantity price breaks are a customer-
 *    context Liquid field that Boost's API cannot index, so each Boost card
 *    fetches its tiers from the theme via the Section Rendering API
 *    (sections/lindy-volume-pricing-data.liquid) — rendered per logged-in
 *    customer, cached per product handle for the page's lifetime.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------
   * 1. Custom navigation relocation
   * ------------------------------------------------------------------ */

  var NAV_SELECTOR = '.lindy-custom-navigation';
  // Ordered by priority. -content is the inner block holding the filter
  // option list in both the desktop tree and the mobile drawer (where it
  // sits below the drawer's toolbar header — see boost-filter-tree-mobile.txt);
  // the outer containers are fallbacks in case Boost's DOM differs.
  var FILTER_TREE_SELECTORS = [
    '.boost-sd__filter-tree-vertical-content',
    '.boost-sd__filter-tree-vertical',
    '.boost-sd__filter-tree',
  ];

  // Boost re-renders wipe injected nodes out of its DOM, which detaches the
  // nav from the document. Keep a reference so it can be re-inserted.
  var navElement = null;
  var navWrapper = null;

  function getNavWrapper() {
    if (!navElement) {
      navElement = document.querySelector(NAV_SELECTOR);
      if (!navElement) return null;

      // Wrapper carries the class that scopes the nav's existing CSS
      // (lindy-filters.css) plus a hook for Boost-specific overrides.
      navWrapper = document.createElement('div');
      navWrapper.className = 'lindy-filters-sidebar lindy-boost-nav';
      navWrapper.appendChild(navElement);
    }
    return navWrapper;
  }

  function findFilterTree() {
    // Boost renders separate filter trees for the desktop sidebar and the
    // mobile drawer. Walk the selectors in priority order and prefer a
    // visible match (the drawer's tree when it is open); if nothing is
    // visible, fall back to the first candidate found.
    var fallback = null;
    for (var i = 0; i < FILTER_TREE_SELECTORS.length; i++) {
      var trees = document.querySelectorAll(FILTER_TREE_SELECTORS[i]);
      for (var j = 0; j < trees.length; j++) {
        if (!fallback) fallback = trees[j];
        if (trees[j].getClientRects().length > 0) return trees[j];
      }
    }
    return fallback;
  }

  function placeCustomNavigation() {
    // Find the tree first: the nav stays untouched in its original block
    // until Boost's filter tree actually exists to receive it.
    var tree = findFilterTree();
    if (!tree) return;

    var wrapper = getNavWrapper();
    if (!wrapper) return;

    if (wrapper.parentElement !== tree || tree.firstElementChild !== wrapper) {
      tree.insertBefore(wrapper, tree.firstChild);
    }
  }

  // Boost's overlay filter drawer is a plain div inside the section tree and
  // cannot stack above the theme's sticky header (the theme's own drawers
  // win because they are <dialog> elements in the browser top layer). While
  // the drawer is open, hide the header instead — the drawer and its
  // backdrop cover the viewport, so this matches the top-layer look.
  var watchedDrawer = null;
  var drawerObserver = null;

  function watchMobileDrawerState() {
    var drawer = document.getElementById('boost-sd_filter-tree-mobile');

    if (!drawer) {
      document.documentElement.classList.remove('lindy-boost-drawer-open');
      if (drawerObserver) {
        drawerObserver.disconnect();
        drawerObserver = null;
      }
      watchedDrawer = null;
      return;
    }

    // style1 renders the filters inline rather than as an overlay drawer;
    // the header should stay visible for it.
    if (drawer.className.indexOf('boost-sd__filter-tree-wrapper--style1') !== -1) return;

    var sync = function () {
      var open = getComputedStyle(drawer).display !== 'none';
      document.documentElement.classList.toggle('lindy-boost-drawer-open', open);
    };

    if (drawer !== watchedDrawer) {
      watchedDrawer = drawer;
      if (drawerObserver) drawerObserver.disconnect();
      drawerObserver = new MutationObserver(sync);
      drawerObserver.observe(drawer, {
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
    }

    sync();
  }

  function applyBoostUiFixes() {
    watchMobileDrawerState();
    placeCustomNavigation();
    hydrateVolumePricing();
  }

  /* ------------------------------------------------------------------
   * 3. Volume pricing for Boost product cards
   * ------------------------------------------------------------------ */

  var VP_SECTION_ID = 'lindy-volume-pricing-data';

  // handle -> string of tier HTML ('' when the product has no tiers for this
  // customer), or a truthy non-string (Promise) while the fetch is in flight.
  var vpCache = Object.create(null);

  function getCardProductHandle(card) {
    var link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;
    var match = (link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function insertVolumePricing(card, html) {
    if (!html || card.querySelector('.lindy-boost-volume-pricing')) return;

    var priceEl = card.querySelector('.boost-sd__product-price');
    if (!priceEl) return;

    var holder = document.createElement('div');
    holder.className = 'lindy-boost-volume-pricing';
    holder.innerHTML = html;
    priceEl.insertAdjacentElement('afterend', holder);
  }

  function fetchVolumePricing(handle) {
    return originalFetch('/products/' + handle + '?section_id=' + VP_SECTION_ID, {
      headers: { Accept: 'text/html' },
    })
      .then(function (response) {
        return response.ok ? response.text() : '';
      })
      .then(function (text) {
        var parsed = document.createElement('div');
        parsed.innerHTML = text;
        var tiers = parsed.querySelector('.volume-pricing');
        vpCache[handle] =
          tiers && tiers.querySelector('.custom-volume-pricing-info__row')
            ? tiers.outerHTML
            : '';
      })
      .catch(function () {
        vpCache[handle] = '';
      });
  }

  function hydrateVolumePricing() {
    var cards = document.querySelectorAll('.boost-sd__product-item');
    if (!cards.length) return;

    var pending = [];

    for (var i = 0; i < cards.length; i++) {
      var handle = getCardProductHandle(cards[i]);
      if (!handle) continue;

      var cached = vpCache[handle];
      if (typeof cached === 'string') {
        insertVolumePricing(cards[i], cached);
      } else if (!cached) {
        vpCache[handle] = fetchVolumePricing(handle);
        pending.push(vpCache[handle]);
      }
      // else: fetch already in flight; the batch below re-runs hydration.
    }

    // Once new fetches settle, inject into whatever cards are on screen then
    // (Boost may have re-rendered them since the fetch started).
    if (pending.length) {
      Promise.all(pending).then(function () {
        requestAnimationFrame(hydrateVolumePricing);
      });
    }
  }

  function watchForBoostRenders() {
    // Fallback that also covers initial load order: re-place the nav
    // whenever Boost (re-)renders, coalesced to one run per frame.
    var scheduled = false;
    var observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        applyBoostUiFixes();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Crossing the mobile/desktop breakpoint swaps which filter tree is
    // visible without any DOM mutation, so re-place on resize too.
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(applyBoostUiFixes, 150);
    });

    applyBoostUiFixes();

    // The nav is CSS-hidden in its original position to avoid a flash before
    // relocation; if Boost hasn't rendered a filter tree by now, reveal it
    // where it is so it's never lost.
    setTimeout(function () {
      var nav = navElement || document.querySelector(NAV_SELECTOR);
      if (nav && !nav.closest('.lindy-filters-sidebar')) {
        nav.classList.add('lindy-nav-reveal');
      }
    }, 4000);

    // Also register through Boost's documented hook once its bundle loads.
    var attempts = 0;
    var timer = setInterval(function () {
      attempts += 1;
      var boost = /** @type {any} */ (window).boostWidgetIntegration;
      if (boost && typeof boost.regisCustomization === 'function') {
        clearInterval(timer);
        boost.regisCustomization(applyBoostUiFixes, 'filter');
      } else if (attempts > 100) {
        clearInterval(timer);
      }
    }, 300);
  }

  /* ------------------------------------------------------------------
   * 2. Theme cart drawer on Boost add-to-cart
   * ------------------------------------------------------------------ */

  // Boost's product-card button carries --select-option (its "add to cart /
  // select options" action button); older/other placements use *add-to-cart*.
  var BOOST_ATC_SELECTOR =
    '.boost-sd__button--select-option, [class*="boost-sd"][class*="add-to-cart"]';
  var CLICK_WINDOW_MS = 5000;
  var lastBoostAtcClick = 0;

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (target instanceof Element && target.closest(BOOST_ATC_SELECTOR)) {
        lastBoostAtcClick = Date.now();
      }
    },
    true
  );

  // Boost's button can submit a plain form POST to /cart/add, which Shopify
  // answers with a redirect to the cart page. Take over that submit and add
  // via ajax instead so the theme drawer can open.
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.closest('[class*="boost-sd"]')) return;

      var action = form.getAttribute('action') || '';
      if (action.indexOf('/cart/add') === -1) return;

      var idInput = form.querySelector('[name="id"]');
      if (!idInput || !idInput.value) return;

      event.preventDefault();
      lastBoostAtcClick = Date.now();
      submitCartAddForm(form);
    },
    true
  );

  // Section ids of the theme's cart drawer contents; bundling them into the
  // add request makes Shopify return their rendered HTML in the same response
  // (same as the theme's product-form.js), so the drawer fills instantly
  // instead of re-fetching after it opens.
  function getCartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component').forEach(function (item) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        ids.push(item.dataset.sectionId);
      }
    });
    return ids;
  }

  // POST to /cart/add.js and hand the rendered drawer sections to the theme.
  // Rejects only when the add itself failed; an unparseable success body
  // still opens the drawer (the add went through).
  function postCartAdd(body, headers) {
    return originalFetch('/cart/add.js', {
      method: 'POST',
      headers: headers,
      body: body,
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart add failed');
        return response.json().catch(function () {
          return null;
        });
      })
      .then(function (result) {
        handleBoostCartAdd(result && result.sections);
      });
  }

  // Reads inventory-tracking info for the post-add reconciliation pass (see
  // reconcileBoostBackorder below). `source` is either the clicked
  // button/form or, for Boost's own native controls that don't carry the
  // theme's data-backorder-* attributes, falls back to the product card's
  // inventory badge.
  function getBoostInventoryInfo(source) {
    var card = source instanceof Element ? source.closest('.boost-sd__product-item') : null;
    var status = card && card.querySelector('[data-backorder-state]');
    var managed =
      !!status ||
      (source instanceof Element && source.getAttribute('data-backorder-inventory-managed') === 'true');
    var inventoryQuantity = Math.max(
      Number(
        (status && status.getAttribute('data-backorder-inventory-quantity')) ||
        (source instanceof Element && source.getAttribute('data-backorder-inventory-quantity')) ||
        0
      ),
      0
    );

    return { managed: managed, inventoryQuantity: inventoryQuantity };
  }

  // Republishes the `_bo_lines` cart attribute after a Boost add. Loaded
  // dynamically because this file is a classic script (see file header) and
  // lindy-backorder.js is an ES module. Best-effort: a failed or blocked
  // import just skips reconciliation, like any other network failure here.
  function reconcileBoostBackorder(variantId, source) {
    var info = getBoostInventoryInfo(source);
    if (!info.managed) return;

    var numericVariantId = Number(variantId);
    if (!isFinite(numericVariantId)) return;

    import('@theme/lindy-backorder')
      .then(function (module) {
        module.reconcileBackorderProperties(numericVariantId, info.inventoryQuantity);
      })
      .catch(function () {
        /* best-effort; ignore load/network failures */
      });
  }

  function addVariantToCart(variantId, source) {
    var payload = { items: [{ id: Number(variantId), quantity: 1 }] };
    var sectionIds = getCartSectionIds();
    if (sectionIds.length) payload.sections = sectionIds.join(',');

    return postCartAdd(JSON.stringify(payload), {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }).then(function () {
      reconcileBoostBackorder(variantId, source);
    });
  }

  function withPendingState(button, promise) {
    button.disabled = true;
    button.classList.add('lindy-boost-atc--adding');
    var done = function () {
      button.disabled = false;
      button.classList.remove('lindy-boost-atc--adding');
    };
    return promise.then(done, function (error) {
      done();
      throw error;
    });
  }

  // Resolve a clicked button to the variant to add and where to send the
  // shopper if the add fails (stale variant / sold out since render).
  // Two button flavours share this flow:
  //  - the custom add-to-cart button rendered by Boost's product item
  //    templates (see boost-atc-button.txt / boost-list-product-info.txt),
  //    which carries data-variant-id;
  //  - Boost Turbo buttons, which declare their action as JSON in
  //    data-metadata ({"action":{"addToCart":{"productId":"<variant id>"}}} —
  //    see boost-product-info.txt). Only addToCart actions are intercepted;
  //    quickView and other actions pass through.
  function getBoostAtcAction(button) {
    if (button.matches('.lindy-boost-atc[data-variant-id]')) {
      return {
        variantId: button.getAttribute('data-variant-id'),
        fallbackUrl: button.getAttribute('data-product-url') || '/cart',
      };
    }

    var meta = button.getAttribute('data-metadata');
    try {
      meta = JSON.parse(meta);
      // The template json_encodes a string of JSON, so parse twice.
      if (typeof meta === 'string') meta = JSON.parse(meta);
    } catch (error) {
      return null;
    }

    var addToCart = meta && meta.action && meta.action.addToCart;
    if (!addToCart || !addToCart.productId) return null;

    var item = button.closest('.boost-sd__product-item');
    var link = item && item.querySelector('a[href*="/products/"]');
    return {
      variantId: addToCart.productId,
      fallbackUrl: link ? link.href : '',
    };
  }

  // Intercept before Boost's own handler runs: capture phase with
  // stopImmediatePropagation — Boost's list view handles clicks on the whole
  // product item and can swallow the event before it bubbles back to the
  // document.
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;

      var button = target.closest('.lindy-boost-atc[data-variant-id], [data-metadata]');
      if (!button || button.disabled) return;

      var action = getBoostAtcAction(button);
      if (!action) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      withPendingState(button, addVariantToCart(action.variantId, button)).catch(function () {
        if (action.fallbackUrl) window.location.href = action.fallbackUrl;
      });
    },
    true
  );

  function submitCartAddForm(form) {
    var body = new FormData(form);
    var sectionIds = getCartSectionIds();
    if (sectionIds.length) body.append('sections', sectionIds.join(','));

    postCartAdd(body, { Accept: 'application/json' })
      .then(function () {
        reconcileBoostBackorder(body.get('id'), form);
      })
      .catch(function () {
        // Sold out / invalid: fall back to the native flow so Shopify's
        // own error handling applies.
        form.submit();
      });
  }

  function isRecentBoostAdd() {
    return Date.now() - lastBoostAtcClick < CLICK_WINDOW_MS;
  }

  function isCartAddUrl(url) {
    return typeof url === 'string' && url.indexOf('/cart/add') !== -1;
  }

  function handleBoostCartAdd(sections) {
    lastBoostAtcClick = 0;

    originalFetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js')
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        // Matches the theme's CartAddEvent shape (assets/events.js): the cart
        // icon reads detail.data.itemCount; cart-items-component morphs from
        // detail.data.sections when present and re-fetches its section when
        // absent (the Boost-own-button fetch/XHR path has no section HTML).
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: 'boost-sd-integration',
              data: {
                source: 'boost-sd',
                itemCount: cart.item_count,
                sections: sections,
              },
            },
          })
        );
      })
      .catch(function () {
        /* cart refresh failed; still open the drawer */
      })
      .then(openThemeCartDrawer);
  }

  function openThemeCartDrawer() {
    var drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;

    if (typeof drawer.open === 'function') {
      drawer.open();
    } else if (window.customElements && customElements.whenDefined) {
      // cart-drawer.js loads with low priority; wait for the element upgrade.
      customElements.whenDefined('cart-drawer-component').then(function () {
        if (typeof drawer.open === 'function') drawer.open();
      });
    }
  }

  // Boost's buttons post to /cart/add.js themselves; watch network requests
  // that follow a Boost add-to-cart click. Theme adds never set the click
  // flag, so their own flow is untouched.
  var originalFetch = window.fetch;
  window.fetch = function () {
    var args = arguments;
    var input = args[0];
    var url =
      typeof input === 'string' ? input : (input && input.url) || String(input || '');
    var result = originalFetch.apply(this, args);

    if (isCartAddUrl(url) && isRecentBoostAdd()) {
      result
        .then(function (response) {
          if (response.ok && isRecentBoostAdd()) handleBoostCartAdd();
        })
        .catch(function () {});
    }

    return result;
  };

  var originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this._lindyIsCartAdd = isCartAddUrl(String(url || ''));
    return originalOpen.apply(this, arguments);
  };

  var originalSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (this._lindyIsCartAdd) {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300 && isRecentBoostAdd()) {
          handleBoostCartAdd();
        }
      });
    }
    return originalSend.apply(this, arguments);
  };

  /* ------------------------------------------------------------------ */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchForBoostRenders);
  } else {
    watchForBoostRenders();
  }
})();
